import React, { useEffect, useRef, useState } from 'react';

function random(min, max) {
  return Math.random() * (max - min) + min;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

const CozyConnections = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const dpiRef = useRef(1);
  const [isVisible, setIsVisible] = useState(true);

  // Array of alternate colors that may appear (rarely)
  const alternateColors = [
    { r: 255, g: 140, b: 100 }, // Warm orange/coral
    { r: 156, g: 106, b: 222 }, // Soft purple
    { r: 255, g: 107, b: 129 }, // Pink
    { r: 72, g: 199, b: 116 },  // Green
    { r: 255, g: 193, b: 7 },   // Amber/yellow
    { r: 33, g: 150, b: 243 },  // Light blue
    { r: 171, g: 71, b: 188 }   // Purple
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let resizeObserver;

    function setCanvasSize() {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      let cssWidth = rect.width;
      let cssHeight = rect.height;
      
      // Check if we're in a profile section
      const isProfileSection = parent.closest('#profile-section') !== null;
      const isProfileLoading = parent.closest('.profile-loading-wrapper') !== null;
      const hasProfileCozyClass = parent.classList.contains('profile-cozy');
      
      // Force dimensions for profile section
      if (isProfileSection || isProfileLoading || hasProfileCozyClass) {
        // Use explicit dimensions from inline styles or fallback
        if (!cssWidth || cssWidth < 100) {
          cssWidth = 350; // Profile section default width
        }
        if (!cssHeight || cssHeight < 100) {
          cssHeight = window.innerHeight || 800; // Full viewport height
        }
        setIsVisible(true); // Always show in profile section
      } else {
        // For header, check width
        cssHeight = cssHeight || 56;
        const shouldShow = cssWidth >= 300;
        setIsVisible(shouldShow);
        if (!shouldShow) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          return;
        }
      }
      
      const dpr = clamp(window.devicePixelRatio || 1, 1, 2);
      dpiRef.current = dpr;
      canvas.style.width = cssWidth + 'px';
      canvas.style.height = cssHeight + 'px';
      canvas.width = Math.floor(cssWidth * dpr);
      canvas.height = Math.floor(cssHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function initParticles() {
      const width = canvas.clientWidth || 0;
      const targetDensity = 0.06; // particles per px of height
      const baseCount = Math.round((width / 18) * targetDensity);
      const count = clamp(baseCount, 18, 60);

      particlesRef.current = Array.from({ length: count }).map(() => {
        // Rarely assign alternate color (1.6% chance)
        const useAlt = Math.random() < 0.016;
        return {
          x: random(0, canvas.width / dpiRef.current),
          y: random(0, canvas.height / dpiRef.current),
          vx: random(-0.25, 0.25),
          vy: random(-0.25, 0.25),
          r: random(1, 2.2),
          // Assign a random alternate color if using alternate, otherwise null for default blue
          color: useAlt ? alternateColors[Math.floor(Math.random() * alternateColors.length)] : null
        };
      });
    }

    function draw() {
      if (!isVisible) return;
      const width = canvas.width / dpiRef.current;
      const height = canvas.height / dpiRef.current;
      ctx.clearRect(0, 0, width, height);

      // No background fill; let the header's white background show through

      const particles = particlesRef.current;
      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          const maxDist = 120;
          if (dist < maxDist) {
            const alpha = (1 - dist / maxDist) * 0.35;
            // Use alternate color if either particle uses it (prefer the first one's color)
            const altColor = a.color || b.color;
            if (altColor) {
              ctx.strokeStyle = `rgba(${altColor.r},${altColor.g},${altColor.b},${alpha})`;
            } else {
              ctx.strokeStyle = `rgba(74,144,226,${alpha})`;
            }
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        ctx.beginPath();
        // Use alternate color if particle has one assigned, otherwise default blue
        if (p.color) {
          ctx.fillStyle = `rgba(${p.color.r},${p.color.g},${p.color.b},0.9)`;
        } else {
          ctx.fillStyle = 'rgba(74,144,226,0.9)';
        }
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function step() {
      if (!isVisible) {
        animationRef.current = requestAnimationFrame(step);
        return;
      }
      const width = canvas.width / dpiRef.current;
      const height = canvas.height / dpiRef.current;
      const particles = particlesRef.current;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        // gentle wrap-around
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        if (p.y < -10) p.y = height + 10;
        if (p.y > height + 10) p.y = -10;
      }

      draw();
      animationRef.current = requestAnimationFrame(step);
    }

    let resizeTimeout;
    function handleResize() {
      cancelAnimationFrame(animationRef.current);
      clearTimeout(resizeTimeout);
      setCanvasSize();
      initParticles();
      // debounce restart
      resizeTimeout = setTimeout(() => {
        draw();
        animationRef.current = requestAnimationFrame(step);
      }, 80);
    }

    // Initial setup - check for profile loading wrapper
    const checkProfileLoading = () => {
      const parent = canvas.parentElement;
      if (!parent) return false;
      const hasProfileCozyClass = parent.classList.contains('profile-cozy');
      const isProfileLoading = parent.closest('.profile-loading-wrapper') !== null;
      const isProfileSection = parent.closest('#profile-section') !== null;
      return isProfileSection || isProfileLoading || hasProfileCozyClass;
    };
    
    setCanvasSize();
    const isInProfile = checkProfileLoading();
    if (isVisible || isInProfile) {
      if (isInProfile) setIsVisible(true);
      initParticles();
      draw();
    }
    animationRef.current = requestAnimationFrame(step);
    
    // Also setup after delays to ensure parent has dimensions
    const delayedInit1 = setTimeout(() => {
      setCanvasSize();
      const inProfile = checkProfileLoading();
      if (isVisible || inProfile) {
        if (inProfile) setIsVisible(true);
        initParticles();
        draw();
      }
    }, 50);
    
    const delayedInit2 = setTimeout(() => {
      setCanvasSize();
      const inProfile = checkProfileLoading();
      if (isVisible || inProfile) {
        if (inProfile) setIsVisible(true);
        initParticles();
        draw();
      }
    }, 200);

    // Observe parent size changes precisely
    if (canvas.parentElement && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        // Use requestAnimationFrame to break the synchronous resize loop
        requestAnimationFrame(() => {
          handleResize();
        });
      });
      resizeObserver.observe(canvas.parentElement);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(delayedInit1);
      clearTimeout(delayedInit2);
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (resizeObserver) {
        try { 
          resizeObserver.disconnect(); 
        } catch (e) {
          // Ignore disconnect errors - observer may already be disconnected
          console.debug('ResizeObserver disconnect error (safe to ignore):', e);
        }
      }
    };
  }, [isVisible]);

  return (
    <canvas
      ref={canvasRef}
      className="cozy-canvas"
      aria-hidden="true"
      style={{ visibility: isVisible ? 'visible' : 'hidden' }}
    />
  );
};

export default CozyConnections;


